import React, { useEffect, useState } from "react";
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import CustomInput from "Components/Custom/CustomInput";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { getCategories, getSubCategories } from "slices/thunks";
import { useDebounce } from "Components/Hooks/useDebounce";
import { clearSubCategories } from "slices/subcategories/reducer";
import { ErrorToast } from "utils/toast";

interface Props {
  show: boolean;
  onCloseClick: () => void;
  onFilterChange: (
    selectedCategory: string | null,
    selectedSubcategory: number[]
  ) => void;
}

const ItemsFilter: React.FC<Props> = ({
  show,
  onCloseClick,
  onFilterChange,
}) => {
  const { categories, isLoadingCategory } = useAppSelector(
    (stat) => stat.category
  );
  const { subcategories } = useAppSelector((stat) => stat.subcategory);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // Implement debouncing if needed
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<number[]>([]);

  const dispatch = useAppDispatch();

  const handleSubcategoryChange = (subcategoryId: number) => {
    setSelectedSubcategory((prev) =>
      prev.includes(subcategoryId)
        ? prev.filter((s) => s !== subcategoryId)
        : [...prev, subcategoryId]
    );
  };

  const handleApply = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCategory) {
      ErrorToast("Please select at least one filter option.");
      return;
    }
    onFilterChange(selectedCategory, selectedSubcategory);
    onCloseClick();
  };

  const handleClear = () => {
    setSelectedSubcategory([]);
    setSelectedCategory(null);
    onFilterChange(null, []);
    onCloseClick();
  };

  useEffect(() => {
    if (show) {
      dispatch(
        getCategories({
          search: searchTerm,
        })
      );
    }
  }, [dispatch, debouncedSearchTerm]);

  return (
    <Offcanvas direction="end" isOpen={show} toggle={onCloseClick}>
      <OffcanvasHeader className="bg-greyF5" toggle={onCloseClick}>
        <div className="text-black2D font-primary fw-medium fs-16">Filters</div>
      </OffcanvasHeader>

      <form className="d-flex flex-column justify-content-end h-100">
        <OffcanvasBody className="pt-3">
          <CustomInput
            label="Category"
            type="select"
            name="category"
            placeholder="Search or select a category"
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              dispatch(clearSubCategories());
              dispatch(
                getSubCategories({
                  category_id: Number(e.target.value),
                })
              );
            }}
            onBlur={() => {}}
            value={selectedCategory || ""}
            onSearch={(e) => {
              setSearchTerm(e);
            }}
            dropdownOptions={categories.map((cat) => ({
              label: cat.name,
              value: cat.id,
            }))}
            isLoadingOptions={isLoadingCategory}
          />

          {/* STATUS */}

          <Label className="text-black2D font-primary fw-normal fs-14">
            Subcategory
          </Label>
          <Row className="g-2">
            {selectedCategory && subcategories.length !== 0 && (
              <Row className="g-2">
                {subcategories.map((subCat, index) => (
                  <Col key={index} md={6}>
                    <div className="d-flex align-items-center">
                      <Input
                        className="form-check-input me-2 p-0"
                        type="checkbox"
                        checked={selectedSubcategory.includes(subCat.id)}
                        onChange={() => handleSubcategoryChange(subCat.id)}
                        id={`${subCat.id}-${index}`}
                        style={{ width: 20, height: 20 }}
                      />
                      <Label
                        className="text-black2D font-primary fw-normal fs-16 text-capitalize m-0 p-0"
                        htmlFor={`${subCat.id}-${index}`}
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        }}
                      >
                        {subCat.name}
                      </Label>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </Row>
          {!selectedCategory && (
            <div className="text-secondary font-primary fw-normal fs-14">
              Please select a category to see subcategories.
            </div>
          )}
          {selectedCategory && subcategories.length === 0 && (
            <div className="text-secondary font-primary fw-normal fs-14">
              No subcategories available for the selected category.
            </div>
          )}
        </OffcanvasBody>

        {/* FOOTER */}
        <div className="offcanvas-footer border-top p-3 text-center hstack gap-2">
          <button
            type="button"
            className="btn bg-greyF5 text-black2D fs-14 fw-normal font-primary w-100 br-7"
            onClick={handleClear}
          >
            Clear
          </button>

          <button
            type="submit"
            className="btn bg-primary text-white fs-14 fw-normal font-primary w-100 br-7"
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </form>
    </Offcanvas>
  );
};

export default React.memo(ItemsFilter);
